import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  type ButtonProps,
  type DialogProps,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import React, { useId } from "react";
import { useTranslation } from "react-i18next";

export interface AdminDialogAction {
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick: () => void;
  color?: ButtonProps["color"];
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit";
}

export type AdminDialogErrorOperation = "create" | "update" | "delete";

export interface AdminDialogProps extends Omit<DialogProps, "children" | "onClose" | "title"> {
  title: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  error?: React.ReactNode;
  errorOperation?: AdminDialogErrorOperation;
  errorEntity?: string;
  onClose: () => void;
  cancelAction?: AdminDialogAction;
  secondaryAction?: AdminDialogAction;
  primaryAction?: AdminDialogAction;
  loading?: boolean;
  contentSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
}

const renderAction = (
  action: AdminDialogAction,
  variant: ButtonProps["variant"],
  defaultColor: ButtonProps["color"],
) => (
  <Button
    onClick={action.onClick}
    variant={variant}
    color={action.color ?? defaultColor}
    disabled={action.disabled || action.loading}
    type={action.type ?? "button"}
    startIcon={action.loading ? <CircularProgress color="inherit" size={18} /> : action.icon}
  >
    {action.label}
  </Button>
);

/**
 * Shared admin dialog chrome. Page-specific content and state stay with the owning page.
 */
export const AdminDialog: React.FC<AdminDialogProps> = ({
  title,
  icon,
  children,
  error,
  errorOperation,
  errorEntity,
  onClose,
  cancelAction,
  secondaryAction,
  primaryAction,
  loading = false,
  contentSx,
  actionsSx,
  "aria-labelledby": ariaLabelledBy,
  ...dialogProps
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const isBusy = Boolean(
    loading || cancelAction?.loading || secondaryAction?.loading || primaryAction?.loading,
  );
  const formattedError =
    error && errorOperation && errorEntity
      ? t(`common.dialogErrors.${errorOperation}Failed`, {
          entity: errorEntity,
          error,
        })
      : error;

  return (
    <Dialog
      {...dialogProps}
      aria-labelledby={ariaLabelledBy ?? titleId}
      fullWidth={dialogProps.fullWidth ?? true}
      onClose={isBusy ? undefined : onClose}
    >
      <DialogTitle
        id={titleId}
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center" color="primary.main">
            {icon}
            <Typography variant="h6" component="span" color="inherit" fontWeight={700}>
              {title}
            </Typography>
          </Stack>
          <IconButton
            aria-label={t("common.close")}
            onClick={onClose}
            disabled={isBusy}
            edge="end"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      {formattedError && (
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 1.5 } }}>
          <Alert severity="error">{formattedError}</Alert>
        </Box>
      )}
      <DialogContent
        sx={[
          {
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 },
            "& > :first-of-type": { mt: { xs: 1, sm: 1.5 } },
            "& > :last-child": { mb: { xs: 1, sm: 1.5 } },
          },
          ...(contentSx ? (Array.isArray(contentSx) ? contentSx : [contentSx]) : []),
        ]}
      >
        {children}
      </DialogContent>
      {(cancelAction || secondaryAction || primaryAction) && (
        <DialogActions
          sx={[
            {
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              gap: 1,
              flexDirection: { xs: "column-reverse", sm: "row" },
              "& > .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
            },
            ...(actionsSx ? (Array.isArray(actionsSx) ? actionsSx : [actionsSx]) : []),
          ]}
        >
          <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
          {cancelAction && renderAction(cancelAction, "outlined", "primary")}
          {secondaryAction && renderAction(secondaryAction, "outlined", "primary")}
          {primaryAction && renderAction(primaryAction, "contained", "primary")}
        </DialogActions>
      )}
    </Dialog>
  );
};
